import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Mail, Phone, ArrowRight } from 'lucide-react';
import { TEAM_IMAGES } from './ops/teamImages';

const chairmanVision = {
  name: "",
  title: "Chairman Vision",
  message: `Track0tic was conceived with a simple but powerful belief: every medicine delivery should be verifiable, compliant, and trustworthy. By combining intelligent sensors, real-time monitoring, and advanced analytics, Track0tic brings a new level of accountability to pharmaceutical logistics — enabling stakeholders to ensure that medicines maintain their required conditions and reach their destination safely.

Our vision extends beyond tracking shipments. We aim to build a system that provides true delivery verification — confirming not only where a package traveled, but whether it was handled correctly, protected from tampering, and maintained within required environmental conditions.

When medicine integrity is protected, patient safety is protected.`
};

const teamMembers = [
  {
    name: "Rackesh Langer",
    role: "Mentor & Advisor",
    title: "Mentor, Guidance & PCB Design",
    bio: "A graduate of Birla Institute of Technology, Mesra (1977), Rackesh brings over 45 years of progressive expertise across process control instrumentation, enterprise computing, LAN/WAN networking, and embedded systems. He provides strategic mentorship to Track0tic and personally leads the design and development of the project's custom PCB hardware.",
    skills: ["PCB Design", "Embedded Systems", "Instrumentation", "Networking", "Mentorship"],
    experience: "45+",
    education: "BIT Mesra, 1977",
    email: "Rackesh.langer@trackotic.com",
    phone: null,
    imageKey: "rackesh",
    color: "from-blue-500 to-indigo-600"
  },
  {
    name: "Deepak",
    role: "Chief Technology Officer (CTO)", 
    title: "Chief Technology Officer",
    bio: "A seasoned backend architect with 15+ years delivering high-performance enterprise systems. Expert in Node.js, PHP (Laravel/CodeIgniter), MySQL, and RESTful APIs. Has driven backend solutions across fintech, pharma, and logistics — currently engineering Track0tic's core platform and data infrastructure.",
    skills: ["Node.js", "PHP / Laravel", "MySQL", "REST APIs", "CI/CD"],
    experience: "15+",
    education: "BIT, IGNOU Patna",
    email: "Deepak@trackotic.com",
    phone: "",
    imageKey: null,
    initials: "D",
    color: "from-emerald-500 to-teal-600"
  },
  {
    name: "Sarthak",
    role: "Tech Lead",
    title: "Lead Android / Mobile Engineer",
    bio: "Android specialist with 5+ years of experience, currently leading mobile development for Punjab National Bank under Nityo Infotech & Infosys. Expert in Kotlin, Jetpack Compose, MVVM, and fintech-grade app security. Brings deep mobile expertise to Track0tic's patient-facing and logistics apps.",
    skills: ["Kotlin / Java", "Jetpack Compose", "MVVM", "Fintech", "Play Store"],
    experience: "5+",
    education: "B.Tech, RIT",
    email: "Sarthak@trackotic.com",
    phone: "",
    imageKey: null,
    initials: "S",
    color: "from-orange-500 to-red-600"
  }
];

export default function TeamPage() {
  return (
    <div data-testid="team-page" className="min-h-screen bg-white pt-16">
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(59,130,246,0.3),transparent_50%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(239,68,68,0.2),transparent_50%)]" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <Badge className="mb-6 bg-white/10 text-white border-white/20">
              OUR TEAM
            </Badge>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              The <span className="italic text-blue-400">People</span> Behind
              <span className="block">Track<span className="text-red-500">0</span>tic</span>
            </h1>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto">
              A team of seasoned engineers and technologists building the future of pharmaceutical logistics.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Chairman's Vision */}
      <section className="py-12 bg-white" data-testid="chairman-section">
        <div className="max-w-5xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white rounded-2xl shadow-xl overflow-hidden"
          >
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-8 py-4">
              <h2 className="text-xl font-semibold text-white">Chairman's Vision</h2>
            </div>
            <div className="p-8 md:p-12">
              <div className="prose prose-lg max-w-none">
                {chairmanVision.message.split('\n\n').map((paragraph, idx) => (
                  <p key={idx} className="text-gray-600 leading-relaxed mb-4 last:mb-0">
                    {paragraph.includes('Track0tic') 
                      ? paragraph.split('Track0tic').map((part, i, arr) => (
                          <span key={i}>
                            {part}
                            {i < arr.length - 1 && <>Track<span className="text-red-500">0</span>tic</>}
                          </span>
                        ))
                      : paragraph
                    }
                  </p>
                ))}
              </div>
              <div className="mt-8 pt-6 border-t border-gray-200 flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white font-bold text-xl">
                  AK
                </div>
                <div>
                  <div className="font-semibold text-gray-900">{chairmanVision.name}</div>
                  <div className="text-sm text-gray-500">
                    {chairmanVision.title}, Track<span className="text-red-500">0</span>tic
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Team Members Grid */}
      <section className="py-20 bg-white" data-testid="team-members-section">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Meet Our Team
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Dedicated professionals combining decades of experience in technology, engineering, and healthcare logistics.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {teamMembers.map((member, index) => (
              <motion.div
                key={member.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group bg-white rounded-2xl border border-gray-200 overflow-hidden hover:shadow-xl transition-shadow duration-300"
                data-testid={`team-member-${index}`}
              >
                {/* Header with gradient */}
                <div className={`relative h-32 bg-gradient-to-br ${member.color}`}>
                  <div className="absolute -bottom-12 left-1/2 transform -translate-x-1/2">
                    <div className="w-24 h-24 rounded-full border-4 border-white overflow-hidden shadow-lg">
                      {member.imageKey ? (
                        <img 
                          src={TEAM_IMAGES[member.imageKey]} 
                          alt={member.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className={`w-full h-full flex items-center justify-center bg-gradient-to-br ${member.color} text-white font-bold text-2xl`}>
                          {member.initials}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="pt-16 pb-6 px-6 text-center">
                  <Badge className="mb-2 bg-gray-100 text-gray-700 border-gray-200 text-xs">
                    {member.role}
                  </Badge>
                  <h3 className="font-heading text-xl font-bold text-gray-900 mb-1">
                    {member.name}
                  </h3>
                  <p className="text-sm text-gray-500 mb-4">{member.title}</p>
                  
                  <p className="text-sm text-gray-600 leading-relaxed mb-4 line-clamp-4">
                    {member.bio}
                  </p>

                  {/* Skills */}
                  <div className="flex flex-wrap justify-center gap-1.5 mb-4">
                    {member.skills.map((skill) => (
                      <span 
                        key={skill}
                        className="text-xs px-2 py-1 bg-blue-50 text-blue-700 rounded-full"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>

                  {/* Experience Badge */}
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-50 rounded-full mb-4">
                    <span className="text-2xl font-bold text-gray-900">{member.experience}</span>
                    <span className="text-xs text-gray-500 text-left leading-tight">
                      Years<br />Experience
                    </span>
                    <span className="text-xs text-gray-400 ml-2">• {member.education}</span>
                  </div>

                  {/* Contact Links */}
                  <div className="flex justify-center gap-3 pt-4 border-t border-gray-100">
                    {member.email && (
                      <a 
                        href={`mailto:${member.email}`}
                        className="p-2 rounded-lg bg-gray-100 text-gray-600 hover:bg-blue-100 hover:text-blue-600 transition-colors"
                        title={member.email}
                      >
                        <Mail className="w-4 h-4" />
                      </a>
                    )}
                    {member.phone && (
                      <a 
                        href={`tel:${member.phone}`}
                        className="p-2 rounded-lg bg-gray-100 text-gray-600 hover:bg-green-100 hover:text-green-600 transition-colors"
                        title={member.phone}
                      >
                        <Phone className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Join Us CTA */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-gray-800">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
              Join the Track<span className="text-red-500">0</span>tic Team
            </h2>
            <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
              We're always looking for talented individuals passionate about healthcare technology 
              and making a real impact on patient safety.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a 
                href="mailto:careers@trackotic.com"
                className="inline-flex items-center gap-2 px-8 py-4 bg-white text-gray-900 font-semibold rounded-lg hover:bg-gray-100 transition-colors"
              >
                <Mail className="w-5 h-5" />
                careers@trackotic.com
              </a>
              <Link to="/contact">
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-white text-white hover:bg-white/10 h-14 px-8 rounded-lg"
                >
                  Contact Us
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
